(() => {
  function mount(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const abort = new AbortController();
    const listen = (target, type, handler, options = {}) => target.addEventListener(type, handler, { ...options, signal: abort.signal });
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const fine = matchMedia('(pointer: fine)');
    const toggle = root.querySelector('.motion-toggle');
    const access = root.querySelector('.store-access');
    let destroyed = false;
    let paused = false, timer, last = null, frame = 0, lastFrame = 0, elapsed = 0, idleUntil = 0;
    let size = { width: root.clientWidth, height: root.clientHeight };
    const reset = () => { clearTimeout(timer); root.style.setProperty('--echo-strength', '0'); last = null; };
    const allowed = () => !destroyed && root.isConnected && !paused && !reduced.matches && !document.hidden && root.dataset.motion === 'true' && !root.querySelector('form:focus-within') && !access.open;
    const state = () => {
      reset();
      root.classList.toggle('motion-off', paused || reduced.matches || document.hidden || root.dataset.motion !== 'true');
      toggle.hidden = root.dataset.motion !== 'true';
      toggle.textContent = reduced.matches ? 'Reduced motion' : paused ? 'Resume motion' : 'Pause motion';
      toggle.setAttribute('aria-pressed', String(paused || reduced.matches));
      root.classList.toggle('access-open', access.open);
      syncAmbient();
    };
    // Touch screens get a slow focal drift, independent of mouse input.
    function ambient(now) {
      frame = 0;
      if (fine.matches || !allowed()) return;
      if (now - lastFrame >= 32) {
        elapsed += lastFrame ? Math.min(now - lastFrame, 80) : 0;
        lastFrame = now;
        if (now > idleUntil) {
          const phase = elapsed / 12000 * Math.PI * 2;
          root.style.setProperty('--focus-x', (size.width * (.73 + .06 * Math.sin(phase))) + 'px');
          root.style.setProperty('--focus-y', (Math.min(260, Math.max(170, size.height * .3)) + 20 * Math.cos(phase)) + 'px');
          root.style.setProperty('--echo-x', (8 * Math.sin(phase)) + 'px');
          root.style.setProperty('--echo-y', (3 * Math.cos(phase)) + 'px');
          root.style.setProperty('--echo-strength', String(.04 + .04 * Math.sin(phase)));
        }
      }
      frame = requestAnimationFrame(ambient);
    }
    function syncAmbient() {
      cancelAnimationFrame(frame); frame = 0; lastFrame = 0;
      if (!fine.matches && allowed()) frame = requestAnimationFrame(ambient);
    }
    const resize = new ResizeObserver(() => { size = { width: root.clientWidth, height: root.clientHeight }; });
    resize.observe(root);
    root.classList.add('motion-ready');
    listen(toggle, 'click', () => { paused = !paused; state(); });
    listen(reduced, 'change', state);
    listen(fine, 'change', state);
    listen(document, 'visibilitychange', state);
    const formFocus = () => {
      const focused = Boolean(root.querySelector('form:focus-within'));
      if (root.classList.contains('form-focused') === focused) return;
      root.classList.toggle('form-focused', focused); reset(); syncAmbient();
    };
    listen(root, 'focusin', formFocus);
    listen(root, 'focusout', () => queueMicrotask(formFocus));
    listen(access, 'toggle', state);
    const close = root.querySelector('.access-close');
    close.hidden = false;
    const closeAccess = () => { access.open = false; access.querySelector('summary').focus(); };
    listen(close, 'click', closeAccess);
    listen(root, 'keydown', event => { if (event.key === 'Escape' && access.open) closeAccess(); });
    const position = event => {
      const rect = root.getBoundingClientRect();
      const point = { x: event.clientX - rect.left, y: event.clientY - rect.top };
      root.style.setProperty('--focus-x', point.x + 'px');
      root.style.setProperty('--focus-y', point.y + 'px');
      return point;
    };
    const settle = () => { clearTimeout(timer); timer = setTimeout(() => root.style.setProperty('--echo-strength', '0'), 250); };
    listen(root, 'pointermove', event => {
      if ((!fine.matches && event.pointerType !== 'touch') || !allowed() || event.target.closest('button,input,a,summary')) return;
      if (event.pointerType === 'touch') idleUntil = performance.now() + 2500;
      const point = position(event), dx = last ? point.x - last.x : 0, dy = last ? point.y - last.y : 0;
      root.style.setProperty('--echo-x', Math.max(-32, Math.min(32, -dx * .8)) + 'px');
      root.style.setProperty('--echo-y', Math.max(-16, Math.min(16, -dy * .55)) + 'px');
      root.style.setProperty('--echo-strength', String(Math.min(.32, Math.hypot(dx, dy) / 75)));
      last = point; settle();
    }, { passive: true });
    listen(root, 'pointerdown', event => {
      if (event.pointerType !== 'touch' || !allowed() || event.target.closest('button,input,a,summary')) return;
      idleUntil = performance.now() + 2500;
      last = position(event);
      root.style.setProperty('--echo-x', '-16px');
      root.style.setProperty('--echo-y', '6px');
      root.style.setProperty('--echo-strength', '.28');
      settle();
    }, { passive: true });
    listen(root, 'pointerleave', event => { if (event.pointerType !== 'touch') reset(); });
    listen(window, 'pagehide', () => { reset(); cancelAnimationFrame(frame); frame = 0; });
    listen(window, 'pageshow', state);
    listen(document, 'shopify:section:unload', event => { if (event.target.contains(root)) { destroyed = true; reset(); cancelAnimationFrame(frame); resize.disconnect(); abort.abort(); } });
    state();
    root.querySelector('[data-form-result]')?.focus();
  }
  const init = () => document.querySelectorAll('[data-password-scene]').forEach(mount);
  init();
  document.addEventListener('shopify:section:load', init);
})();
